import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart";
import { LineChart, Line, CartesianGrid, XAxis, YAxis } from "recharts";
import { useAppStore, useInvestmentData, useTeamData, useCostData, CURRENCY_RATES } from "../stores/appStore";
import { Header } from "./header";
import { Calculator, TrendingUp, Users, DollarSign, Target, BarChart3, Settings, Activity } from "lucide-react";

interface UnitEconomicsInputs {
  monthlyRevenuePerUser: number;
  churnRate: number;
  customerAcquisitionCost: number;
  averageCustomerLifetime: number;
  grossMargin: number;
  salesCycleLength: number;
  viralCoefficient: number;
}

interface ScenarioPreset {
  name: string;
  description: string;
  inputs: UnitEconomicsInputs;
}

// Helper functions for smart calculations
const calculateWeightedARPU = (investmentData: any) => {
  const { soloPrice, teamPrice, unlimitedPrice } = investmentData;

  // Customer mix ratios from main calculator (30% solo, 50% team, 20% unlimited)
  const soloRatio = 0.3;
  const teamRatio = 0.5;
  const unlimitedRatio = 0.2;

  const weightedARPU = (
    (soloPrice * soloRatio) +
    (teamPrice * teamRatio) +
    (unlimitedPrice * unlimitedRatio)
  );

  return weightedARPU;
};

const getBusinessModelChurnRate = (avgPrice: number) => {
  if (avgPrice > 500) return 3.0;      // Enterprise SaaS
  if (avgPrice > 200) return 4.0;      // B2B SaaS
  if (avgPrice > 50) return 6.0;       // SMB SaaS
  return 8.0;                          // Consumer apps
};

const calculateDynamicCAC = (avgPrice: number) => {
  const cacMultiplier = avgPrice > 500 ? 0.8 :   // Enterprise: 80% of annual revenue
                        avgPrice > 200 ? 1.0 :   // B2B: 100% of annual revenue
                        avgPrice > 50 ? 1.2 :    // SMB: 120% of annual revenue
                        1.5;                     // Consumer: 150% of annual revenue

  return avgPrice * 12 * cacMultiplier;
};

const calculateViralCoefficient = (investmentData: any) => {
  const { soloGrowthRate, teamGrowthRate, unlimitedGrowthRate } = investmentData;

  const viralCoeff = (
    (soloGrowthRate * 0.3) +
    (teamGrowthRate * 0.5) +
    (unlimitedGrowthRate * 0.2)
  ) / 100 + 1;

  return Math.max(1.0, viralCoeff);
};

const calculateRealContributionMargin = (
  monthlyRevenuePerUser: number,
  teamRoles: any[],
  operatingCosts: any[]
) => {
  // Variable costs as percentage (payment processing, etc.)
  const variableCostPercentage = 0.15; // 15% variable costs
  const variableCostsPerUser = monthlyRevenuePerUser * variableCostPercentage;

  return monthlyRevenuePerUser - variableCostsPerUser;
};

const calculateTotalMonthlyFixedCosts = (
  monthlyOpex: number,
  teamRoles: any[],
  operatingCosts: any[]
) => {
  const teamFixedCosts = teamRoles.reduce((total: number, role: any) => {
    const monthlyHeadcount = role.headcount.reduce((sum: number, count: number) => sum + count, 0) / role.headcount.length;
    return total + (monthlyHeadcount * role.salary / 12 * 0.7); // 70% fixed
  }, 0);

  const fixedOperatingCosts = operatingCosts
    .filter((cost: any) => cost.category !== 'marketing')
    .reduce((total: number, cost: any) => {
      const avgMonthly = cost.monthlyCosts.reduce((sum: number, cost: number) => sum + cost, 0) / cost.monthlyCosts.length;
      return total + avgMonthly;
    }, 0);

  return monthlyOpex + teamFixedCosts + fixedOperatingCosts;
};

const generateSmartPresets = (investmentData: any): ScenarioPreset[] => {
  const avgPrice = calculateWeightedARPU(investmentData);
  const dynamicCAC = calculateDynamicCAC(avgPrice);
  const businessChurn = getBusinessModelChurnRate(avgPrice);
  const viralCoeff = calculateViralCoefficient(investmentData);

  return [
    {
      name: "Your Business Model",
      description: `Based on your pricing tiers and growth assumptions`,
      inputs: {
        monthlyRevenuePerUser: avgPrice,
        churnRate: businessChurn,
        customerAcquisitionCost: dynamicCAC,
        averageCustomerLifetime: 24,
        grossMargin: 0.75,
        salesCycleLength: avgPrice > 200 ? 60 : 30,
        viralCoefficient: viralCoeff,
      }
    },
    {
      name: "Conservative Scenario",
      description: "Lower growth, higher churn - stress test your model",
      inputs: {
        monthlyRevenuePerUser: avgPrice * 0.9,
        churnRate: businessChurn * 1.5,
        customerAcquisitionCost: dynamicCAC * 1.2,
        averageCustomerLifetime: 18,
        grossMargin: 0.7,
        salesCycleLength: avgPrice > 200 ? 90 : 45,
        viralCoefficient: Math.max(1.0, viralCoeff * 0.8),
      }
    },
    {
      name: "Aggressive Growth",
      description: "Higher growth, lower churn - best case scenario",
      inputs: {
        monthlyRevenuePerUser: avgPrice * 1.2,
        churnRate: Math.max(1.0, businessChurn * 0.7),
        customerAcquisitionCost: dynamicCAC * 0.8,
        averageCustomerLifetime: 36,
        grossMargin: 0.8,
        salesCycleLength: avgPrice > 200 ? 30 : 14,
        viralCoefficient: viralCoeff * 1.3,
      }
    },
    {
      name: "B2B SaaS (Conservative)",
      description: "Realistic for enterprise software with longer sales cycles",
      inputs: {
        monthlyRevenuePerUser: 299,
        churnRate: 3.0,
        customerAcquisitionCost: 1200,
        averageCustomerLifetime: 36,
        grossMargin: 0.75,
        salesCycleLength: 90,
        viralCoefficient: 1.0,
      }
    },
    {
      name: "B2C SaaS (Growth-Focused)",
      description: "Consumer apps with viral potential and lower CAC",
      inputs: {
        monthlyRevenuePerUser: 19.99,
        churnRate: 8.0,
        customerAcquisitionCost: 150,
        averageCustomerLifetime: 18,
        grossMargin: 0.85,
        salesCycleLength: 7,
        viralCoefficient: 1.3,
      }
    },
    {
      name: "Marketplace (Two-Sided)",
      description: "Platforms connecting buyers and sellers",
      inputs: {
        monthlyRevenuePerUser: 49.99,
        churnRate: 5.0,
        customerAcquisitionCost: 300,
        averageCustomerLifetime: 24,
        grossMargin: 0.65,
        salesCycleLength: 30,
        viralCoefficient: 1.1,
      }
    },
    {
      name: "AI Tool (High-Value)",
      description: "Specialized AI tools with premium pricing",
      inputs: {
        monthlyRevenuePerUser: 99,
        churnRate: 4.0,
        customerAcquisitionCost: 800,
        averageCustomerLifetime: 30,
        grossMargin: 0.80,
        salesCycleLength: 45,
        viralCoefficient: 1.0,
      }
    }
  ];
};

export default function UnitEconomicsSimulator() {
  const { currency } = useAppStore();
  const { data: investmentData } = useInvestmentData();
  const { teamRoles } = useTeamData();
  const { operatingCosts } = useCostData();

  // Generate smart defaults on component mount
  const [inputs, setInputs] = useState<UnitEconomicsInputs>(() => {
    const calculatedARPU = calculateWeightedARPU(investmentData);
    const dynamicCAC = calculateDynamicCAC(calculatedARPU);
    const businessChurn = getBusinessModelChurnRate(calculatedARPU);
    const viralCoeff = calculateViralCoefficient(investmentData);

    return {
      monthlyRevenuePerUser: calculatedARPU,
      churnRate: businessChurn,
      customerAcquisitionCost: dynamicCAC,
      averageCustomerLifetime: 24,
      grossMargin: 0.75,
      salesCycleLength: calculatedARPU > 200 ? 60 : 30,
      viralCoefficient: viralCoeff,
    };
  });

  const [selectedPreset, setSelectedPreset] = useState<string>("");
  const [scenarioPresets, setScenarioPresets] = useState<ScenarioPreset[]>([]);

  // Generate dynamic presets when investment data changes
  useEffect(() => {
    const smartPresets = generateSmartPresets(investmentData);
    setScenarioPresets(smartPresets);
  }, [investmentData]);

  // Auto-sync inputs when project data changes
  useEffect(() => {
    const newARPU = calculateWeightedARPU(investmentData);
    const newCAC = calculateDynamicCAC(newARPU);
    const newChurn = getBusinessModelChurnRate(newARPU);
    const newViral = calculateViralCoefficient(investmentData);

    setInputs(prev => ({
      ...prev,
      monthlyRevenuePerUser: newARPU,
      customerAcquisitionCost: newCAC,
      churnRate: newChurn,
      viralCoefficient: newViral,
      salesCycleLength: newARPU > 200 ? 60 : 30,
    }));
  }, [
    investmentData.soloPrice,
    investmentData.teamPrice,
    investmentData.unlimitedPrice,
    investmentData.soloGrowthRate,
    investmentData.teamGrowthRate,
    investmentData.unlimitedGrowthRate,
  ]);

  const formatCurrency = (value: number) => {
    const convertedValue = value * CURRENCY_RATES[currency];
    const currencyConfig = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };
    const config = currencyConfig[currency];
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
    }).format(convertedValue);
  };

  const calculations = useMemo(() => {
    const {
      monthlyRevenuePerUser,
      churnRate,
      customerAcquisitionCost,
      averageCustomerLifetime,
      grossMargin,
      salesCycleLength,
      viralCoefficient,
    } = inputs;

    // Core unit economics calculations
    const annualRevenuePerUser = monthlyRevenuePerUser * 12;
    const monthlyChurnRate = churnRate / 100;
    const monthlyRetentionRate = 1 - monthlyChurnRate;

    // Customer Lifetime Value (LTV)
    const ltv = annualRevenuePerUser * averageCustomerLifetime / 12;

    // LTV/CAC Ratio
    const ltvCacRatio = ltv / customerAcquisitionCost;

    // Payback Period (months)
    const paybackPeriod = customerAcquisitionCost / monthlyRevenuePerUser;

    // Real contribution margin using actual costs
    const realContributionMargin = calculateRealContributionMargin(
      monthlyRevenuePerUser,
      teamRoles,
      operatingCosts
    );

    // Total monthly fixed costs from real data
    const totalMonthlyFixedCosts = calculateTotalMonthlyFixedCosts(
      investmentData.monthlyOpex || 50000,
      teamRoles,
      operatingCosts
    );

    // Break-even customers using real costs
    const realBreakEvenCustomers = realContributionMargin > 0
      ? Math.ceil(totalMonthlyFixedCosts / realContributionMargin)
      : Infinity;

    // Customer profitability
    const monthlyProfitPerCustomer = realContributionMargin - (customerAcquisitionCost / averageCustomerLifetime);
    const annualProfitPerCustomer = monthlyProfitPerCustomer * 12;

    // Growth projections (simplified)
    const months = Array.from({ length: 24 }, (_, i) => i);
    const customerGrowth = months.map(month => {
      const organicGrowth = Math.pow(viralCoefficient, month);
      const customers = Math.floor(100 * organicGrowth); // Starting with 100 customers
      const revenue = customers * monthlyRevenuePerUser;
      const costs = customers * (customerAcquisitionCost / averageCustomerLifetime) + totalMonthlyFixedCosts;
      const profit = revenue * grossMargin - costs;

      return {
        month: month + 1,
        customers,
        revenue,
        costs,
        profit,
        cumulativeProfit: months.slice(0, month + 1).reduce((sum, m) => {
          const c = Math.floor(100 * Math.pow(viralCoefficient, m));
          const r = c * monthlyRevenuePerUser;
          const cost = c * (customerAcquisitionCost / averageCustomerLifetime) + totalMonthlyFixedCosts;
          return sum + (r * grossMargin - cost);
        }, 0)
      };
    });

    return {
      annualRevenuePerUser,
      ltv,
      ltvCacRatio,
      paybackPeriod,
      realContributionMargin,
      totalMonthlyFixedCosts,
      realBreakEvenCustomers,
      monthlyProfitPerCustomer,
      annualProfitPerCustomer,
      customerGrowth,
      monthlyChurnRate,
      monthlyRetentionRate,
    };
  }, [inputs, investmentData.monthlyOpex, teamRoles, operatingCosts]);

  const handleInputChange = (field: keyof UnitEconomicsInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setSelectedPreset(""); // Clear preset when manually adjusting
  };

  const applyPreset = (preset: ScenarioPreset) => {
    setInputs(preset.inputs);
    setSelectedPreset(preset.name);
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900">
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Customer LTV
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {formatCurrency(calculations.ltv)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Lifetime value per customer
                  </p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                    LTV/CAC Ratio
                  </p>
                  <p className={`text-2xl font-bold ${calculations.ltvCacRatio >= 3 ? 'text-green-600 dark:text-green-400' : calculations.ltvCacRatio >= 2 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                    {calculations.ltvCacRatio.toFixed(1)}x
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {calculations.ltvCacRatio >= 3 ? 'Excellent' : calculations.ltvCacRatio >= 2 ? 'Good' : 'Needs improvement'}
                  </p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Payback Period
                  </p>
                  <p className={`text-2xl font-bold ${calculations.paybackPeriod <= 12 ? 'text-green-600 dark:text-green-400' : calculations.paybackPeriod <= 18 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                    {calculations.paybackPeriod.toFixed(1)} months
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Time to recover CAC
                  </p>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Monthly Contribution
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {formatCurrency(calculations.realContributionMargin)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Revenue after variable costs
                  </p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <Calculator className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scenario Selection */}
        <Card className="mb-8 border-0 shadow-sm bg-white dark:bg-slate-800">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Scenario Analysis
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400">
                  Select a scenario to analyze or customize parameters below
                </CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs">
                Auto-generated from your pricing
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {scenarioPresets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    selectedPreset === preset.name
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
                  }`}
                >
                  <div className="space-y-2">
                    <div className={`font-semibold text-sm ${selectedPreset === preset.name ? 'text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-slate-100'}`}>
                      {preset.name}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      {preset.description}
                    </div>
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        ARPU: {formatCurrency(preset.inputs.monthlyRevenuePerUser)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Model Parameters
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400">
                  Adjust key assumptions for your analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm font-medium">Auto-sync enabled</span>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Parameters update automatically when you modify pricing or costs
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="monthlyRevenue" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Monthly Revenue Per User (ARPU)
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-500 dark:text-slate-400 text-sm">
                          {currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'}
                        </span>
                      </div>
                      <Input
                        id="monthlyRevenue"
                        type="number"
                        value={inputs.monthlyRevenuePerUser}
                        onChange={(e) => handleInputChange('monthlyRevenuePerUser', parseFloat(e.target.value) || 0)}
                        className="pl-12 h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                      />
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Weighted average from your pricing tiers
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="churnRate" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Monthly Churn Rate (%)
                    </Label>
                    <Input
                      id="churnRate"
                      type="number"
                      step="0.1"
                      value={inputs.churnRate}
                      onChange={(e) => handleInputChange('churnRate', parseFloat(e.target.value) || 0)}
                      className="h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Based on your business model
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="cac" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Customer Acquisition Cost (CAC)
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-500 dark:text-slate-400 text-sm">
                          {currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'}
                        </span>
                      </div>
                      <Input
                        id="cac"
                        type="number"
                        value={inputs.customerAcquisitionCost}
                        onChange={(e) => handleInputChange('customerAcquisitionCost', parseFloat(e.target.value) || 0)}
                        className="pl-12 h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                      />
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Calculated from pricing and benchmarks
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="lifetime" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Average Customer Lifetime (Months)
                    </Label>
                    <Input
                      id="lifetime"
                      type="number"
                      value={inputs.averageCustomerLifetime}
                      onChange={(e) => handleInputChange('averageCustomerLifetime', parseFloat(e.target.value) || 0)}
                      className="h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Expected customer relationship duration
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="margin" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Gross Margin (%)
                    </Label>
                    <Input
                      id="margin"
                      type="number"
                      step="1"
                      value={inputs.grossMargin * 100}
                      onChange={(e) => handleInputChange('grossMargin', parseFloat(e.target.value) / 100 || 0)}
                      className="h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Revenue after cost of goods sold
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Growth Factors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label htmlFor="salesCycle" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Sales Cycle Length (Days)
                  </Label>
                  <Input
                    id="salesCycle"
                    type="number"
                    value={inputs.salesCycleLength}
                    onChange={(e) => handleInputChange('salesCycleLength', parseFloat(e.target.value) || 0)}
                    className="h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="viral" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Viral Coefficient
                  </Label>
                  <Input
                    id="viral"
                    type="number"
                    step="0.1"
                    value={inputs.viralCoefficient}
                    onChange={(e) => handleInputChange('viralCoefficient', parseFloat(e.target.value) || 0)}
                    className="h-10 border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Organic growth multiplier
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analysis Dashboard */}
          <div className="lg:col-span-2 space-y-8">
            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                        Break-even Customers
                      </p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        {calculations.realBreakEvenCustomers.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Customers needed for profitability
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/20 rounded-lg">
                      <Users className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                        Annual Profit/Customer
                      </p>
                      <p className={`text-2xl font-bold ${calculations.annualProfitPerCustomer > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(calculations.annualProfitPerCustomer)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Net profit per customer annually
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Growth Projection Chart */}
            <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  24-Month Growth Projection
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400">
                  Customer acquisition, revenue, and profitability over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    customers: { label: "Customers", color: "hsl(var(--chart-1))" },
                    revenue: { label: "Revenue", color: "hsl(var(--chart-2))" },
                    profit: { label: "Profit", color: "hsl(var(--chart-3))" },
                  }}
                  className="w-full h-[350px]"
                >
                  <LineChart data={calculations.customerGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => formatCurrency(value).replace(/[^\d.]/g, '')}
                    />
                    <ChartTooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }}
                      content={<ChartTooltipContent />}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="customers"
                      stroke="hsl(var(--chart-1))"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulativeProfit"
                      stroke="hsl(var(--chart-3))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>

            {/* Health Assessment */}
            <Card className="border-0 shadow-sm bg-white dark:bg-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Unit Economics Health Check
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400">
                  How your metrics compare to industry benchmarks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">LTV/CAC Ratio</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {calculations.ltvCacRatio >= 3 ? 'Excellent - Strong unit economics' :
                         calculations.ltvCacRatio >= 2 ? 'Good - Room for optimization' :
                         calculations.ltvCacRatio >= 1 ? 'Fair - Needs attention' : 'Poor - Requires immediate action'}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      calculations.ltvCacRatio >= 3 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      calculations.ltvCacRatio >= 2 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {calculations.ltvCacRatio.toFixed(1)}x
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">Payback Period</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {calculations.paybackPeriod <= 6 ? 'Excellent - Fast recovery' :
                         calculations.paybackPeriod <= 12 ? 'Good - Reasonable timeline' :
                         calculations.paybackPeriod <= 18 ? 'Fair - Monitor closely' : 'Slow - Consider optimization'}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      calculations.paybackPeriod <= 12 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      calculations.paybackPeriod <= 18 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {calculations.paybackPeriod.toFixed(1)} months
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">Monthly Churn</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {inputs.churnRate <= 3 ? 'Excellent - Very low churn' :
                         inputs.churnRate <= 5 ? 'Good - Industry standard' :
                         inputs.churnRate <= 8 ? 'Fair - Above average' : 'High - Requires attention'}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      inputs.churnRate <= 5 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      inputs.churnRate <= 8 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {inputs.churnRate}%
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">Gross Margin</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {inputs.grossMargin >= 0.8 ? 'Excellent - High margins' :
                         inputs.grossMargin >= 0.7 ? 'Good - Healthy margins' :
                         inputs.grossMargin >= 0.6 ? 'Fair - Standard margins' : 'Low - Consider pricing strategy'}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      inputs.grossMargin >= 0.7 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      inputs.grossMargin >= 0.6 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {(inputs.grossMargin * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}